import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/services/supabase-client.service';
import { useFacility } from '@/contexts/FacilityContext';
import { logger } from '@/lib/logger';

/**
 * Sets up global realtime subscriptions for care-web.
 * Listens to care-relevant tables and invalidates React Query cache on changes.
 */
export function useGlobalRealtime() {
    const { facilityId } = useFacility();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!facilityId) return;

        logger.debug('[Realtime] Setting up care subscriptions for facility:', facilityId);
        const channel = supabase
            .channel(`care-facility-${facilityId}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'residents', filter: `facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['residents', facilityId] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'sis_entries', filter: `facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['sis', facilityId] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'care_alerts', filter: `facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['alerts', facilityId] });
                },
            )
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'vital_signs' },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['vitals', facilityId] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'wound_assessments', filter: `facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['wounds', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['wound-stats', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['wound-approval-queue', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['wound'] });
                    void queryClient.invalidateQueries({ queryKey: ['wound-history'] });
                    void queryClient.invalidateQueries({ queryKey: ['wound-trend'] });
                    void queryClient.invalidateQueries({ queryKey: ['dash-wounds', facilityId] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'wound_approval_audit_log', filter: `facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['wound-approval-queue', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['wound'] });
                    void queryClient.invalidateQueries({ queryKey: ['dash-wounds', facilityId] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'inventory_items', filter: `facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['inventory-items', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['inventory-stats', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['inventory-alerts', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['inventory-categories', facilityId] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'facility_beds' },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['beds', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['rooms', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['occupancy-stats', facilityId] });
                    void queryClient.invalidateQueries({
                        predicate: (query) => query.queryKey[0] === 'room-beds',
                    });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'facility_rooms', filter: `facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['rooms', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['occupancy-stats', facilityId] });
                    void queryClient.invalidateQueries({
                        predicate: (query) => query.queryKey[0] === 'room-beds',
                    });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'facility_floors', filter: `facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['floors', facilityId] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'cross_sector_data_sharing', filter: `source_facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['residents', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['resident-chart'] });
                    void queryClient.invalidateQueries({ queryKey: ['resident-link-info'] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'cross_sector_data_sharing', filter: `target_facility_id=eq.${facilityId}` },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['residents', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['resident-chart'] });
                    void queryClient.invalidateQueries({ queryKey: ['resident-link-info'] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'resident_medications' },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['all-medications', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['medications'] });
                },
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'care_resident_medications' },
                () => {
                    void queryClient.invalidateQueries({ queryKey: ['med-plan', facilityId] });
                    void queryClient.invalidateQueries({ queryKey: ['medications'] });
                },
            )
            .subscribe((status) => {
                logger.debug('[Realtime] Care channel status:', status);
            });

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [facilityId, queryClient]);
}
